/**
 * The signed-in user's own profile, straight from Supabase.
 *
 * The same object /auth/login returns in its body and /auth/me serves on
 * reload, read by the browser instead of round-tripping through Render. This
 * matters more than on any data screen: the profile is what the app waits for
 * before it can render anything, so with a sleeping backend it is the single
 * request that makes login take a cold start (measured: 53.7s).
 *
 * Tenant scoping is not repeated here: users_select (migration 000004) admits
 * the row whose auth_user_id equals auth.uid(), so the caller can only ever
 * read themselves anyway. That requires a Supabase session — see
 * lib/supabaseSession — which is why this returns null rather than throwing
 * and every caller keeps its backend path.
 */

import { supabase } from '../supabaseClient';
import logger from '../utils/logger';
import type { User, UserRole } from '../types/models';

// The columns /auth/login exposes (backend USER_PUBLIC_COLUMNS + the shop and
// group labels it joins in). password_hash is not among them, and RLS would
// not hand it over regardless.
const SELECT =
  'user_id,name,email,role,shop_id,hiragana_reading,auth_user_id,' +
  'shops!users_shop_id_fkey(name,group_id,corporate_groups(name))';

// PostgREST returns a to-one embed as an object, but the same shape arrives as
// a single-element array often enough that the other direct hooks handle both.
type Embedded<T> = T | T[] | null;

interface EmbeddedGroup {
  name: string | null;
}

interface EmbeddedShop {
  name: string | null;
  group_id: number | null;
  corporate_groups: Embedded<EmbeddedGroup>;
}

interface ProfileRow {
  user_id: number;
  name: string;
  email: string;
  role: UserRole;
  shop_id: number | null;
  hiragana_reading: string | null;
  auth_user_id: string | null;
  shops: Embedded<EmbeddedShop>;
}

function one<T>(embed: Embedded<T>): T | null {
  if (!embed) return null;
  return Array.isArray(embed) ? embed[0] ?? null : embed;
}

/**
 * Assemble the profile the way /auth/login does.
 *
 * The group is not a column on users: the backend derives it as
 * users.shop_id → shops.group_id (db.get_user_group_id) and only labels it
 * when the shop resolves, which is why a shopless super_admin gets neither a
 * shop_name nor a corporate_group_name there. Reproduced exactly — screens
 * branch on corporate_group_id being absent to pick shop-level over
 * group-level endpoints.
 */
function toUser(row: ProfileRow): User {
  const shop = one(row.shops);
  const group = shop ? one(shop.corporate_groups) : null;

  const profile: User = {
    user_id: row.user_id,
    name: row.name,
    email: row.email,
    role: row.role,
    shop_id: row.shop_id,
    hiragana_reading: row.hiragana_reading,
    auth_user_id: row.auth_user_id,
    corporate_group_id: shop?.group_id ?? null,
  };
  // Optional in the login response too; left undefined rather than null so
  // the object matches what the backend sends field for field.
  if (shop?.name) profile.shop_name = shop.name;
  if (group?.name) profile.corporate_group_name = group.name;
  return profile;
}

/**
 * Read the profile belonging to a Supabase session.
 *
 * @param authUserId `session.user.id` — the auth.users UUID the row is linked
 *   to via public.users.auth_user_id.
 * @returns the profile, or null when it cannot be read directly (no Supabase
 *   client configured, an unlinked account, an expired session). Never throws:
 *   callers treat null as "use the backend".
 */
export async function fetchUserProfileDirect(
  authUserId: string
): Promise<User | null> {
  if (!supabase) return null;

  try {
    const { data, error } = await supabase
      .from('users')
      .select(SELECT)
      .eq('auth_user_id', authUserId)
      .maybeSingle();

    if (error || !data) {
      if (error) {
        logger.warn('Direct profile read failed; using the backend', error.message);
      }
      return null;
    }
    return toUser(data as unknown as ProfileRow);
  } catch (error) {
    logger.warn('Direct profile read failed; using the backend', error);
    return null;
  }
}
